import type { BrandStore } from '@jotta/ui/BrandStore'
import { useBrandStore } from '@jotta/ui/useBrandTheme'
import { AppLayout } from '@jotta/ui/AppLayout'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { WelcomeDialog } from '@jotta/ui/WelcomeDialog'
import { observer } from 'mobx-react-lite'
import type { FC, PropsWithChildren } from 'react'
import { Suspense, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import type { PrivateSection } from './PrivateSection'
import { Header } from './Header'
import { NavBar } from './NavBar'
import { TopNavBar } from './TopNavBar'
import { WebappBanner } from './WebappBanner'
import { useGetCustomer } from '@jotta/grpc-connect-client/customer'
import { OnboardingContext } from '@jotta/ui/Onboarding'

const WebappModuleNav = observer(() => {
  const brandStore = useBrandStore()
  const branding = brandStore

  const moduleNavOverlay = branding.getSlot('moduleNavOverlay')
  const moduleNav = branding.getSlot('moduleNav')

  if (moduleNavOverlay) {
    return (
      <div className="z-appMainBottomToolbar bg-primary sm:px-4">
        <div
          ref={moduleNavOverlay}
          className="flex h-[56px] items-center border-b border-light"
        />
      </div>
    )
  }
  if (moduleNav) {
    return (
      <div className="z-appMainBottomToolbar bg-primary sm:px-4">
        <div
          ref={moduleNav}
          className="flex h-[56px] items-center border-b border-light bg-primary"
        />
      </div>
    )
  }
  return null
})

export const PrivateLayout: FC<
  PropsWithChildren<{
    section: PrivateSection
    activeElements?: Partial<BrandStore['activeElements']>
  }>
> = observer(function WebappLayout({
  section,
  activeElements,
  children = <Outlet />,
}) {
  const brandStore = useBrandStore()
  const overlayRef = brandStore.slots.overlay
  const showModuleNav =
    section === 'biz' || (section === 'settings' && !brandStore.isMobile)

  useEffect(() => {
    brandStore.activateElements({
      leftNav: true,
      moduleNav: showModuleNav,
      timelineScrubber: false,
      ...activeElements,
    })
  }, [brandStore, showModuleNav, activeElements, section])

  const customer = useGetCustomer().data
  const showUpgradeLink = customer?.promoteUpgrade || false

  const businessName = customer?.business?.name
  const hasOverlay = brandStore.hasOverlay
  return (
    <OnboardingContext.Provider value={true}>
      <AppLayout
        navbar={
          <NavBar
            businessName={businessName}
            accountType={customer?.accountType}
            userCanUpgrade={showUpgradeLink}
          />
        }
        banner={<WebappBanner />}
        moduleNav={<WebappModuleNav />}
        header={<Header />}
        overlayRef={hasOverlay ? overlayRef : undefined}
      >
        <Suspense fallback={<LoadingOverlay open />}>
          <TopNavBar section={section} />
          {children}
        </Suspense>
        <WelcomeDialog />
      </AppLayout>
    </OnboardingContext.Provider>
  )
})
